import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import * as styles from './view-report-tenure-insights.module.scss';
import { arrow_prev } from '../../../../assets/img';
import { BasicButton, BasicTable } from '../../../../assets/common';
import { convertRoleForPosition, handleDownloadFile, paginationConfig } from '../../../../utils/helper';
import { talentAnalyticApi } from '../../../../services/talentAnalyticApi';
import { notification, Spin } from 'antd';
import {
  TA_TENURE_TABS,
  TA_YEARS_LABEL,
  TA_YEARS_OPTIONS,
  TI_REPORT_COLUMNS,
  TYPES_HAVE_RANGES,
  YISG_AGING_REPORT_TYPE,
  DEFAULT_CRITICAL_POSITION,
} from '../../talent-analytics.constant';
import { useHistory } from 'react-router-dom/cjs/react-router-dom';
import { isEmpty } from 'lodash';
import { USER_ACCESS_MESSAGE } from '../../../../utils/constants';

const ROWS_LIMIT = 25;
const TABLE_STYLE = {
  borderRadius: 6,
};

const ViewReportTenureInsights = ({ reportType, filterMasterData, tabFilters, totalTalent, currentChartData, resetHoveredValueRef }) => {
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [reports, setReports] = useState({
    res: [],
    total: 10,
  });
  const [page, setPage] = useState(1);
  const history = useHistory();

  const handleClickBack = () => {
    resetHoveredValueRef();
    history.push(window.location.path);
  };

  const yearsInDisplay = useMemo(() => {
    if (!TYPES_HAVE_RANGES.includes(reportType)) return;
    const options = TA_YEARS_OPTIONS.find((i) => i.value === tabFilters[reportType]);
    return options.label;
  }, [tabFilters, reportType]);

  const filterApplied = useMemo(() => {
    const filters = [];
    Object.keys(filterMasterData).map((key) => {
      if (filterMasterData[key].length) {
        filters.push(key);
      }
    });
    return filters;
  }, [filterMasterData]);

  const getReportType = useMemo(() => {
    if (isEmpty(reportType)) return '';

    let tempVal = '';
    switch (reportType) {
      case TA_TENURE_TABS[0]:
        tempVal = YISG_AGING_REPORT_TYPE.BY_SALARY_GRADE;
        break;
      case TA_TENURE_TABS[1]:
        tempVal = YISG_AGING_REPORT_TYPE.BY_POSITION;
        break;
      case TA_TENURE_TABS[2]:
        tempVal = YISG_AGING_REPORT_TYPE.BY_BUSINESS_UNIT;
        break;
      case TA_TENURE_TABS[3]:
        tempVal = YISG_AGING_REPORT_TYPE.TOP_TALENT_CATEGORY_DYNAMICS;
        break;
      case TA_TENURE_TABS[4]:
        tempVal = YISG_AGING_REPORT_TYPE.SUCCESSORS_APPROACHING_RETIREMENT_DATE;
        break;
      case TA_TENURE_TABS[5]:
        tempVal = YISG_AGING_REPORT_TYPE.SUCCESSOR_STATUS;
        break;
      default:
        break;
    }
    return tempVal;
  }, [reportType]);

  useEffect(() => {
    const handleGetReports = async () => {
      let updatedFilterMasterData;

      if (reportType === TA_TENURE_TABS[1]) {
        updatedFilterMasterData = {
          ...filterMasterData,
          salary_grade: currentChartData?.xAxisName ? [{ value: currentChartData.xAxisName, expanded: true }] : [...filterMasterData.salary_grade],
          critical_position_category: currentChartData?.stackName ? [currentChartData.stackName] : DEFAULT_CRITICAL_POSITION,
        };
      } else updatedFilterMasterData = filterMasterData;

      try {
        setLoading(true);
        const body = {
          filters: updatedFilterMasterData,
          report_type: getReportType,
          page,
          limit: ROWS_LIMIT,
          range: [YISG_AGING_REPORT_TYPE.BY_BUSINESS_UNIT].includes(currentChartData?.report_type)
            ? Number(currentChartData?.min)
            : TYPES_HAVE_RANGES.includes(reportType) && tabFilters[reportType]
            ? Number(tabFilters[reportType])
            : undefined,
          business: currentChartData?.business_unit ? [currentChartData?.business_unit] : [],
          total_talents: totalTalent,
        };
        const res = await talentAnalyticApi.fetchViewDetailsByReportType(body);
        if (res.status === 200) {
          setReports(res.data.result);
        }
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false);
      }
    };
    handleGetReports();
  }, [page]);

  const handleDownloadReport = async () => {
    let updatedFilterMasterData;

    if (reportType === TA_TENURE_TABS[1] && !isEmpty(currentChartData)) {
      updatedFilterMasterData = {
        ...filterMasterData,
        salary_grade: currentChartData?.xAxisName ? [{ value: currentChartData.xAxisName, expanded: true }] : [...filterMasterData.salary_grade],
        critical_position_category: [currentChartData.stackName],
      };
    } else updatedFilterMasterData = filterMasterData;

    try {
      setExporting(true);
      const body = {
        filters: updatedFilterMasterData,
        report_type: getReportType,
        page,
        limit: ROWS_LIMIT,
        range: [YISG_AGING_REPORT_TYPE.BY_BUSINESS_UNIT].includes(currentChartData?.report_type)
          ? Number(currentChartData?.min)
          : TYPES_HAVE_RANGES.includes(reportType) && tabFilters[reportType]
          ? Number(tabFilters[reportType])
          : undefined,
        business: currentChartData?.business_unit ? [currentChartData?.business_unit] : [],
        total_talents: totalTalent,
      };
      const res = await talentAnalyticApi.fetchReportTenureInsightUrl(body);
      const file = { url: res?.data?.result, file_name: 'Export Tenure Insights Report' };
      handleDownloadFile(file);
    } catch (error) {
      console.error(error);
      notification.error({
        message: USER_ACCESS_MESSAGE.AN_UNEXPECTED_ERROR,
        duration: 5,
      });
    } finally {
      setExporting(false);
    }
  };

  const handleConvertValue = (value, column) => {
    if (column?.dataIndex === 'role_level') {
      return convertRoleForPosition(value);
    } else {
      return value;
    }
  };

  const handleClickTalentName = (record) => {
    window.open(`/profile/${record?.staff_id}`, '_blank');
  };

  const columns = useMemo(() => {
    return TI_REPORT_COLUMNS.filter(
      (col) =>
        (!col.filterShowName && !col.reportTypeShow) ||
        filterApplied.includes(col.filterShowName) ||
        (col.reportTypeShow && col.reportTypeShow.includes(reportType))
    ).map((col) => {
      const isRedirectToProfile = [TA_TENURE_TABS[0], TA_TENURE_TABS[1], TA_TENURE_TABS[2]].includes(reportType) && col.dataIndex === 'name';
      return {
        ...col,
        align: col.align || 'center',
        render: (text, record) => (
          <div
            className={styles.text}
            style={{ ...col.style, cursor: isRedirectToProfile ? 'pointer' : 'auto' }}
            onClick={isRedirectToProfile ? () => handleClickTalentName(record) : null}
          >
            {text ? handleConvertValue(text, col) : '-'}
          </div>
        ),
      };
    });
  }, [filterApplied, reportType]);

  const pagination = useMemo(() => {
    const onChange = (pageNum) => {
      setPage(pageNum);
    };
    return paginationConfig(reports.total, page, onChange, ROWS_LIMIT);
  }, [reports.total, page]);

  const renderTotalData = ({ currentChartData, totalTalent, totalFromApi }) => {
    if ([YISG_AGING_REPORT_TYPE.BY_BUSINESS_UNIT].includes(currentChartData?.report_type)) return currentChartData?.count;
    if (reportType === TA_TENURE_TABS[1]) return totalFromApi;
    return totalTalent;
  };

  return (
    <div className={styles.wrapper}>
      <button className={styles.btn} onClick={handleClickBack}>
        <img src={arrow_prev} /> <span>Back to Dashboard</span>
      </button>
      <div className={styles.title}>View Report</div>
      <Spin spinning={loading}>
        <div className={styles.content}>
          <div className={styles.topInfo}>
            <div className={styles.info}>
              {currentChartData?.business_unit && (
                <div>
                  {'Business Unit'} <span>{currentChartData?.business_unit}</span>
                </div>
              )}
              {(TYPES_HAVE_RANGES.includes(reportType) || [YISG_AGING_REPORT_TYPE.BY_BUSINESS_UNIT].includes(currentChartData?.report_type)) && (
                <div>
                  {reportType}{' '}
                  <span>
                    {yearsInDisplay
                      ? yearsInDisplay
                      : [YISG_AGING_REPORT_TYPE.BY_BUSINESS_UNIT].includes(currentChartData?.report_type)
                      ? TA_YEARS_LABEL[currentChartData?.min]
                      : '-'}
                  </span>
                </div>
              )}
              <div>
                Total Talents{' '}
                <span>
                  {renderTotalData({
                    currentChartData: currentChartData,
                    totalTalent: totalTalent,
                    totalFromApi: reports.total,
                  }) || 0}
                </span>
              </div>
            </div>
            <Spin spinning={exporting}>
              <BasicButton disabled={!reports.total} mode="teal" onClick={handleDownloadReport}>
                Download Report
              </BasicButton>
            </Spin>
          </div>

          <BasicTable
            rowKey={(record) => `${record.no}`}
            dataSource={reports?.res || []}
            columns={columns}
            pagination={pagination}
            scroll={{ x: 'max-content' }}
            styles={TABLE_STYLE}
          />
        </div>
      </Spin>
    </div>
  );
};

export default ViewReportTenureInsights;

ViewReportTenureInsights.propTypes = {
  reportType: PropTypes.string,
  filterMasterData: PropTypes.object,
  tabFilters: PropTypes.object,
  totalTalent: PropTypes.number,
};
